import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutate } from 'swr';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  Calendar,
  Check,
  CheckCircle2,
  Heart,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Share2,
  Upload,
  Users,
  X,
} from 'lucide-react';

import axiosInstance from '@/lib/axios';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { FormWheelDatePickerField } from '@/components/forms/form-wheel-date-picker-field';
import {
  DATE_VISIBILITY_VALUES,
  coerceDateVisibility,
  getFieldVisibility,
} from '@/components/forms/date-visibility';
import { DateVisibilitySelect } from '@/components/forms/date-visibility-select';
import { FormPhoneField } from '@/components/forms/form-phone-field';
import { ImageCropper } from '@/components/ui/image-cropper';
import { ImageFileInput } from '@/components/forms/image-file-input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PublicUserWithCommunities } from '@/types/profile';
import { ChangePhoneDialog } from '@/components/profile/change-phone-dialog';
import { SocialLinksCircleEditor } from '@/components/social/social-links-circle-editor';

/** Coerce missing/invalid values so icon-only selects never fail Zod. */
const visibilityField = z.preprocess(
  (v) => coerceDateVisibility(typeof v === 'string' ? v : null),
  z.enum(DATE_VISIBILITY_VALUES)
);

const personalInfoSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  middleName: z.string().optional(),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().trim().email('Invalid email address').max(100),
  phone: z.string().min(10, 'Phone number is invalid'),
  dob: z.string().optional().nullable(),
  anniversaryDate: z.string().optional().nullable(),
  dobVisibility: visibilityField,
  anniversaryVisibility: visibilityField,
  emailVisibility: visibilityField,
  phoneVisibility: visibilityField,
  genderVisibility: visibilityField,
  bloodGroupVisibility: visibilityField,
  addressVisibility: visibilityField,
  cityVisibility: visibilityField,
  pincodeVisibility: visibilityField,
  gender: z.string().optional().nullable(),
  bloodGroup: z.string().optional().nullable(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
      pincode: z.string().optional(),
      googleMapLink: z.string().url('Invalid URL').optional().or(z.literal('')),
    })
    .optional()
    .nullable(),
});

type PersonalInfoFormValues = z.infer<typeof personalInfoSchema>;

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((v) => ({
  value: v,
  label: v,
}));

function buildDefaults(user: PublicUserWithCommunities): PersonalInfoFormValues {
  return {
    firstName: user.firstName || '',
    middleName: user.middleName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone ? (user.phone.startsWith('+') ? user.phone : `+${user.phone}`) : '',
    dob: user.dob || null,
    anniversaryDate: user.anniversaryDate || null,
    dobVisibility: getFieldVisibility(user, 'dob'),
    anniversaryVisibility: getFieldVisibility(user, 'anniversary'),
    emailVisibility: getFieldVisibility(user, 'email'),
    phoneVisibility: getFieldVisibility(user, 'phone'),
    genderVisibility: getFieldVisibility(user, 'gender'),
    bloodGroupVisibility: getFieldVisibility(user, 'bloodGroup'),
    addressVisibility: getFieldVisibility(user, 'address'),
    cityVisibility: getFieldVisibility(user, 'city'),
    pincodeVisibility: getFieldVisibility(user, 'pincode'),
    gender: user.gender || '',
    bloodGroup: user.bloodGroup || '',
    address: user.address || {
      line1: '',
      line2: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      googleMapLink: '',
    },
  };
}

export interface PersonalInfoSectionEditorRef {
  save: () => Promise<void>;
  isDirty: () => boolean;
}

interface PersonalInfoSectionEditorProps {
  user: PublicUserWithCommunities;
  onDone: () => void;
  onSavingChange: (saving: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  avatarActions?: ReactNode;
}

export const PersonalInfoSectionEditor = forwardRef<
  PersonalInfoSectionEditorRef,
  PersonalInfoSectionEditorProps
>(function PersonalInfoSectionEditor(
  { user, onDone, onSavingChange, onDirtyChange, avatarActions },
  ref
) {
  const [saving, setSaving] = useState(false);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAvatarImage, setSelectedAvatarImage] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [convertingAvatar, setConvertingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user.image || '');
  const [isEditingNames, setIsEditingNames] = useState(false);
  const namesSnapshotRef = useRef<{
    firstName: string;
    middleName?: string;
    lastName: string;
  } | null>(null);
  const [changePhoneOpen, setChangePhoneOpen] = useState(false);
  const [phoneJustVerified, setPhoneJustVerified] = useState(false);
  const [socialLinks, setSocialLinks] = useState<Record<string, string>>(
    () => (user.socialLinks as Record<string, string> | null | undefined) ?? {}
  );
  const [isSavingSocial, setIsSavingSocial] = useState(false);

  useEffect(() => {
    setAvatarPreview(user.image || '');
  }, [user.image]);

  useEffect(() => {
    setSocialLinks((user.socialLinks as Record<string, string> | null | undefined) ?? {});
  }, [user.socialLinks]);

  const handleSocialLinksChange = async (val: Record<string, string>) => {
    setSocialLinks(val);
    setIsSavingSocial(true);
    try {
      await axiosInstance.patch('/api/user', { socialLinks: val });
      await mutate('/api/user');
    } catch {
      toast.error('Failed to save social links');
    } finally {
      setIsSavingSocial(false);
    }
  };

  const form = useForm<PersonalInfoFormValues>({
    resolver: zodResolver(personalInfoSchema) as Resolver<PersonalInfoFormValues>,
    defaultValues: buildDefaults(user),
  });

  const isDirty = form.formState.isDirty;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || '?';

  const handleAvatarCropComplete = async (croppedBlob: Blob) => {
    setIsUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', croppedBlob, 'cropped-image.webp');
    formData.append('folder', 'profile-photos');
    try {
      const { url } = await postMultipartImageUpload(formData);
      await axiosInstance.patch('/api/user', { image: url });
      await mutate('/api/user');
      setAvatarPreview(url);
      toast.success('Profile photo updated');
      setSelectedAvatarImage(null);
    } catch {
      toast.error('Failed to save profile photo');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setIsUploadingAvatar(true);
    try {
      await axiosInstance.patch('/api/user', { image: '' });
      await mutate('/api/user');
      setAvatarPreview('');
      toast.success('Profile photo removed');
    } catch {
      toast.error('Failed to remove profile photo');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    const valid = await form.trigger();
    if (!valid) {
      throw new Error('Validation failed');
    }

    const parsed = personalInfoSchema.safeParse(form.getValues());
    if (!parsed.success) {
      throw new Error('Validation failed');
    }

    setSaving(true);
    onSavingChange(true);
    try {
      const {
        phone: _phone,
        dobVisibility,
        anniversaryVisibility,
        emailVisibility,
        phoneVisibility,
        genderVisibility,
        bloodGroupVisibility,
        addressVisibility,
        cityVisibility,
        pincodeVisibility,
        ...profilePayload
      } = parsed.data;
      await axiosInstance.patch('/api/user', {
        ...profilePayload,
        fieldVisibility: {
          dob: dobVisibility,
          anniversary: anniversaryVisibility,
          email: emailVisibility,
          phone: phoneVisibility,
          gender: genderVisibility,
          bloodGroup: bloodGroupVisibility,
          address: addressVisibility,
          city: cityVisibility,
          pincode: pincodeVisibility,
        },
      });
      await mutate('/api/user');
      toast.success('Profile updated');
      onDone();
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      toast.error(err.response?.data?.error || 'Failed to save profile');
      throw error;
    } finally {
      setSaving(false);
      onSavingChange(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isDirty: () => form.formState.isDirty,
  }));

  const avatarBusy = isUploadingAvatar || convertingAvatar;
  const nameInputClassName = isEditingNames
    ? '!bg-white dark:!bg-zinc-950'
    : '!bg-muted/70 border-border/60 shadow-none hover:shadow-none dark:!bg-muted/40';

  return (
    <>
      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
          {/* Photo + names */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="relative w-full sm:w-auto flex justify-center sm:block shrink-0">
              <div className="flex flex-col items-center gap-2">
                <div className="relative">
                  <Avatar className="h-[4.75rem] w-[4.75rem] rounded-full shadow-sm ring-2 ring-primary/25">
                    <AvatarImage src={avatarPreview || undefined} className="object-cover" />
                    <AvatarFallback className="rounded-full bg-primary text-primary-foreground text-xl font-black font-display">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  {avatarBusy ? (
                    <div className="absolute inset-0 rounded-full bg-black/45 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 text-white animate-spin" />
                    </div>
                  ) : null}
                  {avatarPreview ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={avatarBusy || saving}
                      title="Remove photo"
                      aria-label="Remove photo"
                      className="absolute -right-1 -top-1 z-10 h-7 w-7 rounded-full border-2 border-background bg-card text-red-600 shadow-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40"
                      onClick={() => void handleRemoveAvatar()}
                    >
                      <X className="h-3.5 w-3.5 stroke-[2.5]" />
                    </Button>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={avatarBusy || saving}
                  className="h-7 gap-1.5 rounded-full border-primary/40 px-2.5 text-xs font-medium text-primary bg-primary/5 hover:bg-primary/10 shadow-sm"
                  onClick={() => avatarFileInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" />
                  Upload photo
                </Button>
                {avatarActions ? (
                  <div className="hidden sm:flex flex-row items-center gap-1.5">
                    {avatarActions}
                  </div>
                ) : null}
                <ImageFileInput
                  ref={avatarFileInputRef}
                  className="hidden"
                  accept="image/*"
                  onPreparedDataUrl={setSelectedAvatarImage}
                  onPreparingChange={setConvertingAvatar}
                  disabled={avatarBusy || saving}
                />
              </div>
              {avatarActions ? (
                <div className="absolute right-0 top-1/2 -translate-y-1/2 sm:hidden flex flex-col items-center gap-1.5">
                  {avatarActions}
                </div>
              ) : null}
            </div>

            <div
              className={cn(
                'relative flex-1 min-w-0 rounded-xl border p-3 pt-9 sm:pt-3 sm:pr-10 transition-colors',
                'border-border/70 bg-muted/50 dark:bg-muted/30'
              )}
            >
              <div className="absolute right-2 top-2 flex items-center gap-1">
                {isEditingNames ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={saving}
                      title="Cancel name changes"
                      aria-label="Cancel name changes"
                      className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        const snap = namesSnapshotRef.current;
                        if (snap) {
                          form.reset(
                            {
                              ...form.getValues(),
                              firstName: snap.firstName,
                              middleName: snap.middleName || '',
                              lastName: snap.lastName,
                            },
                            { keepDefaultValues: true }
                          );
                        }
                        namesSnapshotRef.current = null;
                        setIsEditingNames(false);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      disabled={saving}
                      title="Confirm name"
                      aria-label="Confirm name"
                      className="h-7 w-7 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                      onClick={async () => {
                        const ok = await form.trigger(['firstName', 'middleName', 'lastName']);
                        if (!ok) return;
                        namesSnapshotRef.current = null;
                        setIsEditingNames(false);
                      }}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={saving}
                    title="Edit name"
                    aria-label="Edit name"
                    className="h-7 w-7 rounded-full border-primary/40 text-primary shadow-sm bg-primary/5 hover:bg-primary/10"
                    onClick={() => {
                      const values = form.getValues();
                      namesSnapshotRef.current = {
                        firstName: values.firstName,
                        middleName: values.middleName,
                        lastName: values.lastName,
                      };
                      setIsEditingNames(true);
                      window.setTimeout(() => form.setFocus('firstName'), 0);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <FormInputField
                  control={form.control}
                  name="firstName"
                  label="First Name"
                  placeholder="John"
                  required
                  readOnly={!isEditingNames}
                  disabled={saving}
                  inputClassName={nameInputClassName}
                />
                <FormInputField
                  control={form.control}
                  name="middleName"
                  label="Middle Name"
                  placeholder="Optional"
                  readOnly={!isEditingNames}
                  disabled={saving}
                  inputClassName={nameInputClassName}
                />
                <FormInputField
                  control={form.control}
                  name="lastName"
                  label="Last Name"
                  placeholder="Doe"
                  required
                  readOnly={!isEditingNames}
                  disabled={saving}
                  className="sm:col-start-1"
                  inputClassName={nameInputClassName}
                />
              </div>
            </div>
          </div>

          {/* Same card grid as view mode */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <EditFieldCard
              icon={<Mail className="h-4 w-4" />}
              label="Email"
              action={
                <DateVisibilitySelect control={form.control} name="emailVisibility" iconOnly />
              }
            >
              <FormInputField
                control={form.control}
                name="email"
                label=""
                placeholder="name@example.com"
                type="email"
                required
                disabled={saving}
              />
            </EditFieldCard>

            <EditFieldCard
              icon={<Phone className="h-4 w-4" />}
              label="Phone"
              action={
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving}
                    title="Change phone number"
                    aria-label="Change phone number"
                    className="h-7 gap-1 rounded-full border-primary/40 px-2 text-xs font-medium text-primary bg-primary/5 hover:bg-primary/10"
                    onClick={() => setChangePhoneOpen(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <DateVisibilitySelect control={form.control} name="phoneVisibility" iconOnly />
                </div>
              }
            >
              <FormPhoneField
                control={form.control}
                name="phone"
                label=""
                placeholder="Enter phone number"
                disabled
                required
                description={
                  phoneJustVerified ? (
                    <span className="inline-flex items-center gap-1.5 text-green-600 font-medium">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Verified number
                    </span>
                  ) : undefined
                }
              />
            </EditFieldCard>

            <EditFieldCard
              icon={<Users className="h-4 w-4" />}
              label="Gender"
              action={
                <DateVisibilitySelect control={form.control} name="genderVisibility" iconOnly />
              }
            >
              <FormSelectField
                control={form.control}
                name="gender"
                label=""
                placeholder="Select gender"
                options={GENDER_OPTIONS}
              />
            </EditFieldCard>

            <EditFieldCard
              icon={<Heart className="h-4 w-4" />}
              label="Blood Group"
              action={
                <DateVisibilitySelect control={form.control} name="bloodGroupVisibility" iconOnly />
              }
            >
              <FormSelectField
                control={form.control}
                name="bloodGroup"
                label=""
                placeholder="Select"
                options={BLOOD_GROUP_OPTIONS}
              />
            </EditFieldCard>

            <EditFieldCard
              icon={<Calendar className="h-4 w-4" />}
              label="Birthday"
              action={<DateVisibilitySelect control={form.control} name="dobVisibility" iconOnly />}
            >
              <FormWheelDatePickerField
                control={form.control}
                name="dob"
                label=""
                placeholder="Select date"
              />
            </EditFieldCard>

            <EditFieldCard
              icon={<Calendar className="h-4 w-4" />}
              label="Anniversary"
              action={
                <DateVisibilitySelect
                  control={form.control}
                  name="anniversaryVisibility"
                  iconOnly
                />
              }
            >
              <FormWheelDatePickerField
                control={form.control}
                name="anniversaryDate"
                label=""
                placeholder="Select date"
              />
            </EditFieldCard>

            <EditFieldCard
              icon={<MapPin className="h-4 w-4" />}
              label="Residential Address"
              className="md:col-span-2"
              showChildLabels
              action={
                <DateVisibilitySelect control={form.control} name="addressVisibility" iconOnly />
              }
            >
              <div className="space-y-2.5 w-full">
                <FormInputField
                  control={form.control}
                  name="address.line1"
                  label="Street Address"
                  placeholder="House / Flat No., Street"
                />
                <FormInputField
                  control={form.control}
                  name="address.line2"
                  label="Area / Landmark"
                  placeholder="Optional"
                />
                <div className="grid grid-cols-2 gap-2.5">
                  <FormInputField
                    control={form.control}
                    name="address.country"
                    label="Country"
                    placeholder="Country"
                  />
                  <FormInputField
                    control={form.control}
                    name="address.state"
                    label="State"
                    placeholder="State"
                  />
                  <FormInputField
                    control={form.control}
                    name="address.city"
                    label="City"
                    placeholder="City"
                    labelAction={
                      <DateVisibilitySelect
                        control={form.control}
                        name="cityVisibility"
                        iconOnly
                        hideMessage
                        triggerClassName="h-7 w-7"
                      />
                    }
                  />
                  <FormInputField
                    control={form.control}
                    name="address.pincode"
                    label="Pincode"
                    placeholder="Pincode"
                    labelAction={
                      <DateVisibilitySelect
                        control={form.control}
                        name="pincodeVisibility"
                        iconOnly
                        hideMessage
                        triggerClassName="h-7 w-7"
                      />
                    }
                  />
                </div>
                <FormInputField
                  control={form.control}
                  name="address.googleMapLink"
                  label="Google Maps Link"
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </EditFieldCard>

            <EditFieldCard
              icon={<Share2 className="h-4 w-4" />}
              label="Social Media"
              className="md:col-span-2"
            >
              <SocialLinksCircleEditor
                value={socialLinks}
                onChange={handleSocialLinksChange}
                saving={isSavingSocial}
                size="sm"
              />
            </EditFieldCard>
          </div>

          <ChangePhoneDialog
            open={changePhoneOpen}
            onOpenChange={setChangePhoneOpen}
            currentPhone={form.watch('phone') || user.phone}
            onVerified={async (phone) => {
              form.setValue('phone', phone, { shouldDirty: false });
              setPhoneJustVerified(true);
              await mutate('/api/user');
            }}
          />
        </form>
      </Form>

      {selectedAvatarImage ? (
        <ImageCropper
          imageSrc={selectedAvatarImage}
          onCropComplete={handleAvatarCropComplete}
          onCancel={() => !isUploadingAvatar && setSelectedAvatarImage(null)}
          aspect={1}
        />
      ) : null}
    </>
  );
});

function EditFieldCard({
  icon,
  label,
  action,
  children,
  className,
  showChildLabels = false,
}: {
  icon: React.ReactNode;
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Keep nested form labels visible (e.g. address sub-fields). */
  showChildLabels?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-2xl bg-muted/40 dark:bg-muted/25 border border-border',
        className
      )}
    >
      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground leading-none">
          {label}
        </p>
        <div
          className={cn(
            !showChildLabels &&
              '[&_label]:sr-only [&_.space-y-2]:space-y-0 [&_.space-y-1\\.5]:space-y-0'
          )}
        >
          {children}
        </div>
      </div>
      {action ? <div className="shrink-0 pt-0.5">{action}</div> : null}
    </div>
  );
}
